import { getModeLabel } from '@citizen-walk/shared-types';
import { Badge, Button, Card, CollapsibleCard, Modal, useToast } from '@citizen-walk/shared-ui';
import { Fragment, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { resolveUserAppOrigin } from '../../lib/userAppOrigin';
import { districtNavGroups } from './districtMenu';
import { DistrictEditForm } from './DistrictEditForm';
import { DistrictPublicStatusPanel } from './DistrictPublicStatusPanel';
import { DistrictUrlQrPanel } from './DistrictUrlQrPanel';
import { DistrictShortLinkPanel } from './DistrictShortLinkPanel';
import { useShortLinks } from './useShortLinks';
import {
  archiveDistrict,
  regenerateUrlToken,
  unarchiveDistrict,
  useDistrict,
} from './useDistricts';
import {
  buildDistrictConfigExport,
  downloadDistrictJson,
  serializeDistrictExport,
} from './districtJsonIo';

/**
 * 地区詳細 overview ページ (Sprint BG-Menu 第 3 段で分割)。
 *
 * 旧 DistrictDetailPage (455 行 / 14 panel + 11 link card) を以下 5 サブページに分割し、
 * 本ページは基本情報 + access URL + 各機能サブページへの link 集 (overview) のみを残す:
 *
 *   - /districts/:id            = overview (本ファイル、基本情報 + access URL + link 集)
 *   - /districts/:id/display    = DistrictDisplayPage (ColorRule / MapStyle / DisplayConfig / PostArea / PMTiles)
 *   - /districts/:id/ai         = DistrictAiSettingsPage (Dialogue / Verification / ForceReanalyze)
 *   - /districts/:id/misc       = DistrictMiscPage (DistrictMiscPanel)
 *   - /districts/:id/data-settings = features/data-settings (既存維持)
 *
 * 旧の重複 DistrictDataSettingsPanel (quick-edit 簡易版) は撤去 (data-settings 正式ページに集約)。
 *
 * Sprint BG-Menu (第 3 段) 踏襲: 基本情報の編集 / archive / token 再発行 機能はそのまま維持。
 */
export function DistrictDetailPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const state = useDistrict(districtId ?? null);
  const { state: authState } = useAdminAuth();
  const { toast } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // 短縮アドレス一覧は「参加者用の URL と QR コード」(併記) と短縮アドレス管理で共有する。
  const shortLinks = useShortLinks();

  const isMaster = authState.role === 'master';
  const uid = authState.user?.uid ?? '';

  function handleExportConfig() {
    if (!state.district) return;
    try {
      const config = buildDistrictConfigExport(
        state.district as unknown as Record<string, unknown>,
      );
      const envelope = serializeDistrictExport([config]);
      const date = new Date().toISOString().slice(0, 10);
      downloadDistrictJson(envelope, `district-config-${state.district.id}-${date}.json`);
      toast('success', '地区設定を JSON で書き出しました');
    } catch (e) {
      toast('error', `書き出し失敗: ${(e as Error).message}`);
    }
  }

  async function handleArchive() {
    if (!state.district) return;
    if (
      !confirm(
        `「${state.district.name}」をアーカイブしますか？ (利用者はアクセスできなくなります)`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await archiveDistrict(state.district.id, uid);
      toast('success', '地区をアーカイブしました');
    } catch (e) {
      toast('error', `アーカイブ失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleUnarchive() {
    if (!state.district) return;
    setBusy(true);
    try {
      await unarchiveDistrict(state.district.id, uid);
      toast('success', '地区のアーカイブを解除しました');
    } catch (e) {
      toast('error', `アーカイブ解除に失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleRegenToken() {
    if (!state.district) return;
    if (
      !confirm(
        `参加URLを再発行しますか？ 既存の参加 URL (token: ${state.district.urlToken}) は無効化されます。`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const next = await regenerateUrlToken(state.district.id, uid);
      toast('success', `参加URLを再発行しました: ${next}`);
    } catch (e) {
      toast('error', `再発行失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <header className="mb-6">
        <Link
          to="/districts"
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区一覧
        </Link>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mt-2 break-words">
          地区詳細{state.status === 'ready' ? `: ${state.district.name}` : ''}
        </h1>
      </header>

      {state.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}

      {state.status === 'not-found' && (
        <Card padding="md">
          <p className="text-sm text-slate-700">
            地区が見つかりません (id: <code>{districtId}</code>)。
          </p>
        </Card>
      )}

      {state.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {state.error.message}</p>
        </Card>
      )}

      {state.status === 'ready' && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* 基本情報・配布物・公開状態は折りたたみ (2026-10-19 ユーザー要望)。見出しを押すと
              別ページへ移らずこの場で開く。開いたときに横幅を使えるよう 1 列で全幅に並べる。 */}
          <div className="sm:col-span-full grid grid-cols-1 gap-3">
            <CollapsibleCard
              title="基本情報"
              summary={
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  <span>モード: {getModeLabel(state.district.mode, state.district.modeLabel)}</span>
                  {state.district.archivedAt && <Badge variant="warning">アーカイブ済</Badge>}
                </span>
              }
              storageKey="cw-admin:district-overview:basic"
              testId="district-basic-info-panel"
            >
              {isMaster && (
                <div className="flex gap-2 flex-wrap mb-3">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setEditOpen(true)}
                    disabled={busy}
                  >
                    編集
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void handleRegenToken()}
                    disabled={busy}
                  >
                    参加URLを再発行
                  </Button>
                  {state.district.archivedAt ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void handleUnarchive()}
                      disabled={busy}
                    >
                      アーカイブを解除
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => void handleArchive()}
                      disabled={busy}
                    >
                      アーカイブ（非表示）
                    </Button>
                  )}
                </div>
              )}
              {state.district.archivedAt && (
                <div className="mb-3 p-2 rounded bg-slate-100 text-xs text-slate-700">
                  ⚠️ この地区はアーカイブ済です（参加者はアクセスできません）。
                </div>
              )}
              {/* スマホでは 項目名 → 値 の縦積み、sm 以上で 2 列。値は長い ID でも折り返す。 */}
              <dl className="grid grid-cols-1 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] gap-x-3 gap-y-0.5 sm:gap-y-1 text-xs">
                <dt className="text-slate-500 mt-1.5 sm:mt-0">地区ID</dt>
                <dd className="text-slate-800 break-all">
                  <code>{state.district.id}</code>
                </dd>
                <dt className="text-slate-500 mt-1.5 sm:mt-0">地区名</dt>
                <dd className="text-slate-800 break-words">{state.district.name}</dd>
                <dt className="text-slate-500 mt-1.5 sm:mt-0">モード</dt>
                <dd className="text-slate-800 break-words">
                  {getModeLabel(state.district.mode, state.district.modeLabel)}
                </dd>
                <dt className="text-slate-500 mt-1.5 sm:mt-0">参加URLトークン</dt>
                <dd className="text-slate-800 break-all">
                  <code>{state.district.urlToken}</code>
                </dd>
                <dt className="text-slate-500 mt-1.5 sm:mt-0">投稿範囲の中心（緯度・経度）</dt>
                <dd className="text-slate-800 break-all">
                  {state.district.postAreaCenter
                    ? `(${state.district.postAreaCenter.latitude}, ${state.district.postAreaCenter.longitude})`
                    : '未設定'}
                </dd>
                <dt className="text-slate-500 mt-1.5 sm:mt-0">投稿範囲の半径</dt>
                <dd className="text-slate-800">
                  {state.district.postAreaRadiusM
                    ? `${state.district.postAreaRadiusM} m`
                    : '未設定'}
                </dd>
                <dt className="text-slate-500 mt-1.5 sm:mt-0">地図ダウンロード方法</dt>
                <dd className="text-slate-800">
                  {state.district.mapDownloadPolicy === 'auto'
                    ? '自動'
                    : state.district.mapDownloadPolicy === 'manual'
                      ? '手動'
                      : '手動（既定）'}
                </dd>
                <dt className="text-slate-500 mt-1.5 sm:mt-0">分析機能</dt>
                <dd className="text-slate-800">
                  {(state.district.analysisEnabled ?? true) ? '有効' : '無効'}
                </dd>
                <dt className="text-slate-500 mt-1.5 sm:mt-0">分析画面の表示テンプレ</dt>
                <dd className="text-slate-800 break-all">
                  {state.district.analysisDisplayConfigId ? (
                    <code>{state.district.analysisDisplayConfigId}</code>
                  ) : (
                    '未設定'
                  )}
                </dd>
              </dl>
            </CollapsibleCard>

            {/* 参加者向け URL / QR / PDF 印刷 (旧 access panel 2 種を縦並びで残す) */}
            {/* participantUrlBase = ユーザーアプリ (参加者向け) の origin。resolveUserAppOrigin() が
              VITE_USER_APP_BASE_URL 優先 → 無ければ admin origin から規約変換で user origin を導出
              (localhost:5174→5173 / *-admin→*-user / admin.→app.)。env 未注入でも参加 URL/QR が
              admin ドメインにならない (2026-05-30 ユーザー要望: 普遍的な仕組み)。 */}
            <DistrictUrlQrPanel
              district={state.district}
              participantUrlBase={resolveUserAppOrigin()}
              shortLinks={shortLinks.state.links}
            />

            {/* 短縮アドレス (docs/26) — 会場の PC で打ち込むための短い入口。配布物まわりを 1 か所に
              まとめるため、参加 URL / QR の直下に置く。参加 URL と同じ origin を渡す。 */}
            <DistrictShortLinkPanel
              district={state.district}
              participantUrlBase={resolveUserAppOrigin()}
              shortLinks={shortLinks}
            />

            {/* 公開状態（強い停止）— 基本情報と同じ overview ページに配置 (2026-06-11 ユーザー要望で
              「その他設定」から移設)。master / 当該地区の district 管理者が編集可
              (firestore.rules §8-3 の field-scoped 許可と一致)。 */}
            <DistrictPublicStatusPanel
              district={state.district}
              uid={uid}
              canEdit={
                isMaster ||
                (authState.role === 'district' && authState.districts.includes(state.district.id))
              }
            />
          </div>

          {/* 各機能サブページへの誘導。サイドバーと同じ単一の真実の源 (districtMenu) から
              グループ・並び順・ラベルを生成 (業務フロー順: この地区について(GISデータ) / 設定 / 運用 / データ)。 */}
          {districtNavGroups(state.district.id, {
            streetViewAnalysisEnabled: state.district.streetView?.analysisEnabled === true,
            // ユーザー管理は master 常時表示、地区管理者は master が地区ごとに許可した場合のみ。
            participantAdminVisible:
              isMaster || state.district.participantAdmin?.districtAdminCanManage === true,
          }).map((group) => (
            <Fragment key={group.group}>
              <SectionHeader title={group.group} />
              {group.items.map((item) => (
                <OverviewLink
                  key={item.key}
                  to={item.to}
                  label={item.label}
                  description={item.description}
                />
              ))}
            </Fragment>
          ))}

          {isMaster && (
            <div className="sm:col-span-full mt-1 min-w-0">
              <Button variant="secondary" size="md" onClick={handleExportConfig}>
                📥 地区設定を JSON で書き出し（移送用）
              </Button>
              <p className="mt-1 text-xs text-slate-500">
                この地区の設定（名前・範囲・AI 解析・表示設定など）を JSON
                化します。別環境（本番）の地区一覧 「📤
                地区設定を取込」で新規地区として復元できます。urlToken / PMTiles /
                投稿・参加者データは
                含まれません。参照するAI解析の設定は、移送先で先に取込んでおいてください。
              </p>
            </div>
          )}
        </div>
      )}

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="地区を編集"
        size="lg"
        closeOnOverlayClick={false}
      >
        {state.status === 'ready' && uid ? (
          <DistrictEditForm
            target={state.district}
            uid={uid}
            onSaved={() => setEditOpen(false)}
            onCancel={() => setEditOpen(false)}
          />
        ) : (
          <p className="text-sm text-danger-700">サインインが必要です。</p>
        )}
      </Modal>
    </>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h2 className="col-span-full text-sm font-bold text-slate-700 mt-2 mb-1 pl-1 border-l-2 border-primary-500">
      {title}
    </h2>
  );
}

function OverviewLink({
  to,
  label,
  description,
}: {
  to: string;
  label: string;
  description?: string;
}) {
  return (
    <Card padding="md" className="flex items-center justify-between gap-3 min-w-0">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-800 break-words">{label}</p>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
      <Link
        to={to}
        className="text-sm text-primary-600 hover:text-primary-700 hover:underline shrink-0"
      >
        開く →
      </Link>
    </Card>
  );
}
